import moment from 'moment';
import {
    VALIDATION_TYPES,
    VALIDATION_FIELD_PREFIXES,
    VALIDATION_MESSAGES,
    PERMISSION_PREFIXES,
} from '../constants';
import {
    FIELD_VALIDATION_RULES,
    getLicenseFieldRules,
    EDIT_MODE_SKIP_FIELDS,
} from '../config/validation.config';

export const required = (value) => {
    if (value === null || value === undefined || value === '') return VALIDATION_MESSAGES.REQUIRED;
    if (Array.isArray(value) && value.length === 0) return VALIDATION_MESSAGES.REQUIRED;
    return undefined;
};

export const email = (value) => {
    if (!value) return undefined;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value) ? undefined : VALIDATION_MESSAGES.EMAIL_INVALID;
};

export const minLength = (min) => (value) => {
    if (!value) return undefined;
    return value.length >= min ? undefined : VALIDATION_MESSAGES.USERNAME_MIN_LENGTH;
};

export const maxLength = (max) => (value) => {
    if (!value) return undefined;
    return value.length <= max ? undefined : `Must be no more than ${max} characters`;
};

export const alphanumeric = (value) => {
    if (!value) return undefined;
    const alphanumericRegex = /^[a-zA-Z0-9]+$/;
    return alphanumericRegex.test(value) ? undefined : VALIDATION_MESSAGES.USERNAME_FORMAT;
};

export const eeCodeFormat = (value) => {
    if (!value) return undefined;
    const eeCodeRegex = /^[a-zA-Z0-9~`@#$%*)(_{}:;"\/\[\]',&.\-\s]+$/;
    return eeCodeRegex.test(value) ? undefined : VALIDATION_MESSAGES.EE_CODE_FORMAT;
};

export const licenseFormat = (value) => {
    if (!value) return undefined;
    const licenseRegex = /^[a-zA-Z0-9\s()&]+$/;
    return licenseRegex.test(value) ? undefined : VALIDATION_MESSAGES.LICENSE_FORMAT;
};

export const futureDate = (value) => {
    if (!value) return undefined;
    const selectedDate = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true);
    if (!selectedDate.isValid()) return VALIDATION_MESSAGES.LICENSE_EXPIRED;
    const today = moment().startOf('day');
    return selectedDate.startOf('day').isAfter(today) ? undefined : VALIDATION_MESSAGES.LICENSE_EXPIRED;
};

export const composeValidators = (...validators) => (value) => {
    return validators.reduce((error, validator) => error || validator(value), undefined);
};

const VALIDATOR_REGISTRY = {
    [VALIDATION_TYPES.REQUIRED]: required,
    [VALIDATION_TYPES.EMAIL]: email,
    [VALIDATION_TYPES.MIN_LENGTH]: minLength,
    [VALIDATION_TYPES.ALPHANUMERIC]: alphanumeric,
    [VALIDATION_TYPES.EE_CODE_FORMAT]: eeCodeFormat,
    [VALIDATION_TYPES.LICENSE_FORMAT]: licenseFormat,
    [VALIDATION_TYPES.FUTURE_DATE]: futureDate,
};

export const validatePermissions = (allValues) => {
    const hasAnyPermission = Object.keys(allValues).some((key) => {
        if (
            key.startsWith(PERMISSION_PREFIXES.CHECKBOX) ||
            key.startsWith(PERMISSION_PREFIXES.CAN_LOWER) ||
            key.startsWith(PERMISSION_PREFIXES.CAN_UPPER) ||
            key.startsWith(PERMISSION_PREFIXES.STATE) ||
            key.startsWith(PERMISSION_PREFIXES.TEMPLATE)
        ) {
            return allValues[key] === true;
        }
        return false;
    });

    return hasAnyPermission ? {} : { _error: VALIDATION_MESSAGES.AT_LEAST_ONE_PERMISSION };
};

export const validateLicenses = (licenses) => {
    if (!licenses || licenses.length === 0) {
        return undefined;
    }

    const licenseErrors = [];

    licenses.forEach((license, index) => {
        const licenseError = {};

        if (!license.state) {
            licenseError.state = VALIDATION_MESSAGES.LICENSE_STATE_REQUIRED;
        }

        if (!license.licenseNumber) {
            licenseError.licenseNumber = VALIDATION_MESSAGES.LICENSE_NUMBER_REQUIRED;
        } else {
            const formatError = licenseFormat(license.licenseNumber);
            if (formatError) {
                licenseError.licenseNumber = formatError;
            }
        }

        if (license.expirationDate) {
            const dateError = futureDate(license.expirationDate);
            if (dateError) {
                licenseError.expirationDate = dateError;
            }
        }

        if (Object.keys(licenseError).length > 0) {
            licenseErrors[index] = licenseError;
        }
    });

    return licenseErrors.length > 0 ? licenseErrors : undefined;
};

export const findDuplicateLicenses = (formValues, selectedStates) => {
    if (!selectedStates || selectedStates.length < 2) return [];

    const seen = {};
    const duplicates = [];

    selectedStates.forEach((stateCode) => {
        const number = formValues[`${VALIDATION_FIELD_PREFIXES.LICENSE_NUMBER}${stateCode}`];
        if (number) {
            if (seen[number]) {
                duplicates.push({ number, states: [seen[number], stateCode] });
            } else {
                seen[number] = stateCode;
            }
        }
    });

    return duplicates;
};

class ValidationEngine {
    validateField(fieldName, value, fieldRule) {
        if (!fieldRule) return null;

        const validators = fieldRule.validators || [];
        const params = fieldRule.params || {};
        const messages = fieldRule.messages || {};

        for (const type of validators) {
            const validatorFn = VALIDATOR_REGISTRY[type];
            if (!validatorFn) continue;

            let error;
            if (type === VALIDATION_TYPES.MIN_LENGTH) {
                error = validatorFn(params[VALIDATION_TYPES.MIN_LENGTH])(value);
            } else {
                error = validatorFn(value);
            }

            if (error && messages[type]) {
                return messages[type];
            }
            if (error) return error;
        }

        return null;
    }

    validateBasicFields(values, skipFields = []) {
        const errors = {};

        Object.entries(FIELD_VALIDATION_RULES).forEach(([fieldName, rule]) => {
            if (skipFields.includes(fieldName)) return;

            const value = values[fieldName];
            const isEmpty =
                value === null ||
                value === undefined ||
                value === '' ||
                (Array.isArray(value) && value.length === 0);

            if (rule.required && isEmpty) {
                errors[fieldName] = rule.messages[VALIDATION_TYPES.REQUIRED] || VALIDATION_MESSAGES.REQUIRED;
                return;
            }

            if (isEmpty) return;

            const error = this.validateField(fieldName, value, rule);
            if (error) {
                errors[fieldName] = error;
            }
        });

        return errors;
    }

    validateDynamicLicenseFields(values, selectedStates = []) {
        const errors = {};

        selectedStates.forEach((stateCode) => {
            const rules = getLicenseFieldRules(stateCode);

            Object.entries(rules).forEach(([fieldName, rule]) => {
                const value = values[fieldName];
                const isEmpty =
                    value === null ||
                    value === undefined ||
                    value === '' ||
                    (Array.isArray(value) && value.length === 0);

                if (rule.required && isEmpty) {
                    errors[fieldName] = rule.messages[VALIDATION_TYPES.REQUIRED] || VALIDATION_MESSAGES.REQUIRED;
                    return;
                }

                if (isEmpty) return;

                const error = this.validateField(fieldName, value, rule);
                if (error) {
                    errors[fieldName] = error;
                }
            });
        });

        return errors;
    }

    validateForm(values, { isEditMode = false } = {}) {
        const skipFields = isEditMode ? EDIT_MODE_SKIP_FIELDS : [];

        const errors = this.validateBasicFields(values, skipFields);

        const permErrors = validatePermissions(values);
        if (permErrors._error) {
            errors._error = permErrors._error;
        }

        if (values.licenses && values.licenses.length > 0) {
            const licenseErrors = validateLicenses(values.licenses);
            if (licenseErrors) {
                errors.licenses = licenseErrors;
            }
        }

        const selectedStates =
            Array.isArray(values.adjuster_license_states) && values.adjuster_license_states.length > 0
                ? values.adjuster_license_states
                : Object.keys(values)
                      .filter((k) => k.startsWith('license_number_'))
                      .map((k) => k.replace('license_number_', ''));

        if (selectedStates.length > 0) {
            const dynamicErrors = this.validateDynamicLicenseFields(values, selectedStates);
            Object.assign(errors, dynamicErrors);
        }

        return errors;
    }
}

export const validationEngine = new ValidationEngine();

export const validateAddUserForm = (values) => {
    return validationEngine.validateForm(values, { isEditMode: false });
};

export const validateEditUserForm = (values) => {
    return validationEngine.validateForm(values, { isEditMode: true });
};